import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import { AysClinicIdentifier } from '@bindoc-projects/clinic-domain-lib';
import { BdAdministrativeDistrictId } from '@bindoc-projects/geographic-domain-lib';
import { BdArrayUtil, BdEquals } from '@bindoc-projects/util-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';
import { FILTER_RESOURCE_CLINIC_SN } from '../filter-resource-identifiers';
import type {
  FilterConditionDto,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdCondition
} from '@bindoc-projects/condition-domain-lib';
import type {
  GeoPartsAroundClinicIdValue,
  CenterPointSourceType,
} from './geo-parts-around-clinic-id.shared';
import {
  geoPartsClinicIdFromCondition,
  geoPartsClinicSnToCondition,
} from './geo-parts-around-clinic-id.shared';

export const GEO_PARTS_BY_DRIVING_TIME_AROUND_CLINIC_ID_CONDITION_ID =
  'drivingTimeAroundClinicId';
export const FilterConditionGeoPartsByDrivingTimeAroundClinicIdPluginType: FilterConditionPluginSpecification<
  GeoPartsByDrivingTimeAroundClinicId,
  FilterConditionGeoPartsByDrivingTimeAroundClinicIdDto,
  FilterConditionGeoPartsByDrivingTimeAroundClinicIdDto
> = {
  type: 'drivingTimeAroundClinicId',
  category: 'clinic',
  displayName: {
    de: 'Fahrzeit',
  },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionGeoPartsByDrivingTimeAroundClinicIdDto,
  ): FilterConditionPluginImpl<GeoPartsByDrivingTimeAroundClinicId> {
    return new FilterConditionGeoPartsByDrivingTimeAroundClinicIdPlugin(
      obj.coordinate,
      obj.value,
      obj.clinicId ? AysClinicIdentifier.fromObj(obj.clinicId) : null,
      [
        ...obj.administrativeDistricts.map((it) =>
          BdAdministrativeDistrictId.create(it),
        ),
      ],
      [...obj.postalCodes],
      obj.centerPointSourceType ?? 'clinic',
      obj.virtualClinicId,
      obj.rangeFrom,
    );
  },
  fromCondition(
    jsonObj: FilterConditionGeoPartsByDrivingTimeAroundClinicIdDto,
  ): FilterConditionPluginImpl<GeoPartsByDrivingTimeAroundClinicId> {
    return new FilterConditionGeoPartsByDrivingTimeAroundClinicIdPlugin(
      jsonObj.coordinate,
      jsonObj.value,
      geoPartsClinicIdFromCondition(jsonObj),
      [
        ...jsonObj.administrativeDistricts.map((it) =>
          BdAdministrativeDistrictId.create(it),
        ),
      ],
      [...jsonObj.postalCodes],
      jsonObj.centerPointSourceType ?? 'clinic',
      jsonObj.virtualClinicId,
      jsonObj.rangeFrom,
    );
  },
  newInstance(): FilterConditionPluginImpl<GeoPartsByDrivingTimeAroundClinicId> {
    return new FilterConditionGeoPartsByDrivingTimeAroundClinicIdPlugin(
      null,
      10,
      null,
      [],
      [],
      'clinic',
      undefined,
    );
  },
};

export class FilterConditionGeoPartsByDrivingTimeAroundClinicIdPlugin extends FilterConditionPluginImpl<
  GeoPartsByDrivingTimeAroundClinicId,
  FilterConditionGeoPartsByDrivingTimeAroundClinicIdDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    GeoPartsByDrivingTimeAroundClinicId,
    FilterConditionGeoPartsByDrivingTimeAroundClinicIdDto,
    FilterConditionGeoPartsByDrivingTimeAroundClinicIdDto
  > = FilterConditionGeoPartsByDrivingTimeAroundClinicIdPluginType;

  constructor(
    public coordinate: [number, number],
    public value: number,
    public clinicId: AysClinicIdentifier,
    public administrativeDistricts: BdAdministrativeDistrictId[],
    public postalCodes: string[],
    public centerPointSourceType: CenterPointSourceType = 'clinic',
    public virtualClinicId?: string,
    public rangeFrom?: number,
  ) {
    super(FilterConditionGeoPartsByDrivingTimeAroundClinicIdPluginType.type);
  }

  override clone(): FilterConditionGeoPartsByDrivingTimeAroundClinicIdPlugin {
    return new FilterConditionGeoPartsByDrivingTimeAroundClinicIdPlugin(
      this.coordinate,
      this.value,
      this.clinicId ? this.clinicId.clone() : null,
      [...this.administrativeDistricts.map((it) => it.clone())],
      [...this.postalCodes],
      this.centerPointSourceType,
      this.virtualClinicId,
      this.rangeFrom,
    );
  }

  override equals(
    other: FilterConditionGeoPartsByDrivingTimeAroundClinicIdPlugin,
  ): boolean {
    return (
      super.equals(other) &&
      BdEquals.equals(this.value, other.value) &&
      BdArrayUtil.equals(this.coordinate, other.coordinate)
    );
  }

  override valid(): boolean {
    if (!this.value || !this.coordinate) {
      return false;
    }
    switch (this.centerPointSourceType) {
      case 'virtualClinic':
        return !!this.virtualClinicId;
      case 'address':
        return true;
      default:
        return !!this.clinicId;
    }
  }

  override validationToText(): string {
    if (this.valid()) {
      return '';
    } else {
      return 'Fahrzeit und Standort auswählen';
    }
  }

  override toCondition(): BdCondition | null {
    return new GeoPartsByDrivingTimeAroundClinicIdCondition(this.toValue());
  }

  override toJSON(): FilterConditionGeoPartsByDrivingTimeAroundClinicIdDto | null {
    return {
      type: this.type,
      value: this.value,
      coordinate: this.coordinate,
      clinicId: this.clinicId
        ? { ik: this.clinicId.ik, sn: this.clinicId.sn }
        : null,
      centerPointSourceType: this.centerPointSourceType,
      virtualClinicId: this.virtualClinicId,
      rangeFrom: this.rangeFrom,
      postalCodes: this.postalCodes,
      administrativeDistricts: this.administrativeDistricts.map((it) =>
        it.getNumericValue(),
      ),
    };
  }

  override getExtraChildResources(): Record<string, string[]> {
    // Keyed on the opaque clinicSn, NOT AysClinicIdentifier#toString(): a
    // condition read back from the market-api carries a clinicSn-only identity
    // whose `ik` is empty, and toString() returns null without an ik — so the
    // name lookup was never even requested (BDA-5855).
    const clinicSn = geoPartsClinicSnToCondition(this.clinicId);
    if (!clinicSn) return {};
    return { [FILTER_RESOURCE_CLINIC_SN]: [clinicSn] };
  }

  override toDetailedText(
    resolvedNames?: Map<string, string>,
  ): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items: string[] = [`Fahrzeit: ${this.value} min`];
    const clinicSn = geoPartsClinicSnToCondition(this.clinicId);
    if (clinicSn) {
      items.push(`Klinik: ${resolvedNames?.get(clinicSn) ?? clinicSn}`);
    }
    return { title, items };
  }

  override toText(): string | null {
    return `${this.value} min Fahrzeit`;
  }

  override toValue(): GeoPartsByDrivingTimeAroundClinicId {
    const value = new GeoPartsByDrivingTimeAroundClinicId(
      this.coordinate,
      this.value,
      this.clinicId,
      this.administrativeDistricts,
      this.postalCodes,
    );
    value.centerPointSourceType = this.centerPointSourceType;
    value.virtualClinicId = this.virtualClinicId;
    value.rangeFrom = this.rangeFrom;
    return value;
  }
}

export class GeoPartsByDrivingTimeAroundClinicId
  implements GeoPartsAroundClinicIdValue
{
  public rangeFrom?: number;
  public centerPointSourceType?: CenterPointSourceType;
  public virtualClinicId?: string;

  constructor(
    public coordinate: [number, number],
    public value: number,
    public clinicId: AysClinicIdentifier,
    public administrativeDistricts: BdAdministrativeDistrictId[],
    public postalCodes: string[],
  ) {}
}

export class GeoPartsByDrivingTimeAroundClinicIdCondition extends BdCondition {
  constructor(private _value: GeoPartsByDrivingTimeAroundClinicId) {
    super(GEO_PARTS_BY_DRIVING_TIME_AROUND_CLINIC_ID_CONDITION_ID);
  }

  public override toJSON(): FilterConditionDto {
    return {
      [this.id]: {
        value: this._value.value,
        coordinate: this._value.coordinate,
        // `clinicSn`, not the nested `clinicId` the market-api retired in
        // BDA-5580 — it is REQUIRED on this leaf, so the old key made every
        // save 400 (BDA-5851).
        clinicSn: geoPartsClinicSnToCondition(this._value.clinicId),
        centerPointSourceType: this._value.centerPointSourceType,
        virtualClinicId: this._value.virtualClinicId,
        rangeFrom: this._value.rangeFrom,
        postalCodes: this._value.postalCodes,
        administrativeDistricts: this._value.administrativeDistricts.map((it) =>
          it.getNumericValue(),
        ),
      },
    };
  }
}

interface FilterConditionGeoPartsByDrivingTimeAroundClinicIdDto {
  type: string;
  coordinate: [number, number];
  value: number;
  /** Opaque Standortnummer — the market-api contract since BDA-5580. */
  clinicSn?: string | null;
  /** Pre-BDA-5580 identity, still read so older payloads keep hydrating. */
  clinicId?: { ik: string; sn: string } | null;
  centerPointSourceType?: CenterPointSourceType;
  virtualClinicId?: string;
  rangeFrom?: number;
  administrativeDistricts: number[];
  postalCodes: string[];
}
